<template>
    <div class="EvieChatMessageMarkdown">
        <div class="EvieChatMessageMarkdown__body" @click="handleBodyClick" v-html="renderedMarkdown" />
    </div>
</template>

<style lang="scss">
@import "~highlight.js/styles/github.min.css";

.EvieChatMessageMarkdown {
    &__body {
        font-size: 14px;
        line-height: 1.6;
        word-break: break-word;

        > *:first-child {
            margin-top: 0;
        }

        > *:last-child {
            margin-bottom: 0;
        }

        p {
            margin: 0 0 8px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        h1,
        h2,
        h3 {
            font-weight: 600;
            margin: 12px 0 6px;
            line-height: 1.4;

            &:first-child {
                margin-top: 0;
            }
        }

        h1 {
            font-size: 1.25em;
        }

        h2 {
            font-size: 1.1em;
        }

        h3 {
            font-size: 1em;
        }

        ul,
        ol {
            padding-left: 20px;
            margin: 8px 0;
        }

        li {
            margin: 4px 0;
        }

        code {
            background: var(--eidBgGrayTones-lightest);
            border-radius: 3px;
            padding: 2px 5px;
            font-size: 0.875em;
            font-family: "ui-monospace", "SFMono-Regular", "SF Mono", "Menlo", "Consolas", "Liberation Mono", monospace;
        }

        table {
            border-collapse: collapse;
            width: 100%;
            margin: 8px 0;
        }

        th,
        td {
            border: 1px solid var(--eidTechGray-25);
            padding: 6px 12px;
            text-align: left;
        }

        th {
            background: var(--eidBgGrayTones-lightest);
            font-weight: 600;
        }

        blockquote {
            border-left: 3px solid var(--eidSecureBlue);
            margin: 8px 0;
            padding: 4px 0 4px 12px;
            color: var(--eidTechGray-75);

            > p {
                margin: 0;
            }
        }

        a {
            color: var(--eidSecureBlue);
            text-decoration: underline;

            &:hover {
                opacity: 0.8;
            }
        }

        strong {
            font-weight: 600;
        }

        em {
            font-style: italic;
        }

        hr {
            border: none;
            border-top: 1px solid var(--eidTechGray-25);
            margin: 12px 0;
        }
    }

    &__code-block {
        margin: 8px 0;
        border-radius: 6px;
        overflow: hidden;
        background: var(--eidBgGrayTones-lightest);
    }

    &__code-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;
        background: var(--eidTechGray-10);
    }

    &__code-lang {
        font-family: "ui-monospace", "SFMono-Regular", "SF Mono", "Menlo", "Consolas", monospace;
        font-size: 12px;
        color: var(--eidTechGray-75);
    }

    &__copy-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        font-size: 14px;
        background: none;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        color: var(--eidTechGray);
        opacity: 0;
        transition: opacity 0.15s, color 0.15s, background 0.15s;

        &:hover {
            color: var(--eidQualityBlack);
            background: var(--eidTechGray-25);
        }
    }

    &__code-block:hover &__copy-btn {
        opacity: 1;
    }

    &__pre {
        margin: 0;
        border-radius: 0;
        padding: 12px 16px;
        overflow-x: auto;
        background: var(--eidBgGrayTones-lightest);
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import DOMPurify from 'dompurify';
    import hljs from 'highlight.js';
    import noop from 'lodash/noop';
    import { marked, Renderer } from 'marked';

    import { icon, IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faCheck } from '@fortawesome/pro-light-svg-icons';

    import { faCopy } from '@evidentid/dashboard-commons/assets/custom-icon-definitions';

    const COPY_SVG = icon(faCopy as IconDefinition).html[0];
    const CHECK_SVG = icon(faCheck as IconDefinition).html[0];

    const renderer = new Renderer();
    renderer.code = (code: string, infostring: string | undefined) => {
        const lang = (infostring || '').match(/\S*/)?.[0] || '';
        const language = lang && hljs.getLanguage(lang) ? lang : 'plaintext';
        const highlighted = hljs.highlight(code, { language }).value;
        return `<div class="EvieChatMessageMarkdown__code-block">` +
            `<div class="EvieChatMessageMarkdown__code-header">` +
            `<span class="EvieChatMessageMarkdown__code-lang">${language}</span>` +
            `<button class="EvieChatMessageMarkdown__copy-btn" type="button" aria-label="Copy code">${COPY_SVG}</button>` +
            `</div>` +
            `<pre class="EvieChatMessageMarkdown__pre"><code class="hljs language-${language}">${highlighted}</code></pre>` +
            `</div>\n`;
    };

    marked.use({ renderer, breaks: true, gfm: true });

    export default defineComponent({
        name: 'EvieChatMessageMarkdown',
        props: {
            text: { type: String, required: true },
        },
        computed: {
            renderedMarkdown(): string {
                try {
                    return DOMPurify.sanitize(marked.parse(this.text) as string, { ADD_ATTR: [ 'type' ] });
                } catch {
                    return this.text;
                }
            },
        },
        created() {
            (this as any)._copiedTimers = new Map<Element, ReturnType<typeof setTimeout>>();
        },
        beforeUnmount() {
            (this as any)._copiedTimers.forEach(clearTimeout);
        },
        methods: {
            handleBodyClick(event: MouseEvent) {
                const btn = (event.target as Element).closest('.EvieChatMessageMarkdown__copy-btn');
                if (!btn) {
                    return;
                }
                const code = btn.closest('.EvieChatMessageMarkdown__code-block')?.querySelector('code');
                if (!code) {
                    return;
                }
                navigator.clipboard.writeText(code.textContent ?? '').catch(noop);
                btn.innerHTML = CHECK_SVG;
                const timers: Map<Element, ReturnType<typeof setTimeout>> = (this as any)._copiedTimers;
                const existing = timers.get(btn);
                if (existing) {
                    clearTimeout(existing);
                }
                const timer = setTimeout(() => {
                    btn.innerHTML = COPY_SVG;
                    timers.delete(btn);
                }, 1500);
                timers.set(btn, timer);
            },
        },
    });
</script>
